import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'
import { UpdatePerformanceProcessTemplateAuthorDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

type Props = {
  onClose: () => void
  templateId: string
  authorId?: string
}

export const ReplaceAuthorModal = ({ templateId, authorId, onClose }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [selectedAccessId, setSelectedAccessId] = useState<string | undefined>(authorId)
  const [updateAuthor, { loading }] = useMutation(UpdatePerformanceProcessTemplateAuthorDocument, {
    extractorFn: (data) =>
      data.performanceMutations.updateAuthorPerformanceReviewProcessCustomTemplate,
  })

  const handleReplaceOwner = async () => {
    if (!selectedAccessId) {
      return
    }

    const response = await updateAuthor({
      variables: {
        id: templateId,
        authorId: selectedAccessId,
      },
    })

    const result = handleResult(response, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('errors.generic_error'),
      })
    })

    if (result.success) {
      const canManage = result.data.reviewProcessCustomTemplate?._policies?.manage.can ?? false

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      if (!canManage) {
        navigate(navigation.performanceProcess.reviews.templates.fullPath())
      }
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (loading) return
        onClose()
      }}
      title={i18n.t('performance_process.reviews.templates.owners.replace_modal.title')}
      description={i18n.t('performance_process.reviews.templates.owners.replace_modal.description')}
      primaryAction={{
        label: i18n.t('performance_process.reviews.templates.owners.replace_modal.replace'),
        onClick: handleReplaceOwner,
        disabled: loading || !selectedAccessId,
        loading: loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: loading,
      }}
    >
      <Box gap="s16" flexDirection="column">
        <F0Alert
          title={i18n.t('performance_process.reviews.templates.owners.replace_modal.note_title')}
          description={i18n.t(
            'performance_process.reviews.templates.owners.replace_modal.note_description'
          )}
          variant="warning"
        />
        <EmployeeSelectorV2
          label={i18n.t(
            'performance_process.reviews.templates.owners.replace_modal.selector_label'
          )}
          mandatory
          useAccessId
          affiliations={['employee', 'external']}
          value={selectedAccessId}
          onChange={setSelectedAccessId}
        />
      </Box>
    </F0Dialog>
  )
}
